import React, { useState, useMemo, useEffect } from 'react';
import {
  Plus,
  Power,
  Copy,
  Edit,
  Trash2,
  AlertCircle,
  CheckCircle2,
  ExternalLink,
  Filter,
  Play
} from 'lucide-react';
import * as workflowRuleRouter from '../../../services/workflowRuleStorageRouter';
import { USE_GOBODO_API } from '../../../utils/gobodoUtils';
import { WorkflowRule } from '../../../types/workflowRules';
import WorkflowRuleWizard from './WorkflowRuleWizard';
import { workflowExecutionService } from '../../../services/workflowExecution/workflowExecutionService';
import { getRecordsWithIds } from '../../../utils/recordIdHelper';
import {
  StandardButton,
  StandardSearchBar,
  StandardSelect,
  StandardModal,
  StandardEmptyState,
  useToast
} from '../../standard';

/**
 * WorkflowRulesList Component
 *
 * Displays all workflow rules with filtering, search, and management actions.
 * Supports toggle active/inactive, clone, edit, and delete operations.
 * Includes wizard for creating and editing rules.
 *
 * @component
 */
export default function WorkflowRulesList() {
  const [rules, setRules] = useState<WorkflowRule[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [objectFilter, setObjectFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'list' | 'new' | 'edit'>('list');
  const [selectedRuleId, setSelectedRuleId] = useState<string | undefined>(undefined);

  const loadRules = () => {
    workflowRuleRouter.getAllRulesAsync().then(setRules);
  };

  useEffect(() => {
    loadRules();
  }, []);

  // Test workflow modal state
  const [isTestModalOpen, setIsTestModalOpen] = useState(false);
  const [testRuleId, setTestRuleId] = useState<string>('');
  const [testRecordId, setTestRecordId] = useState<string>('');
  const [testTriggerType, setTestTriggerType] = useState<'create' | 'update'>('update');
  const [testResult, setTestResult] = useState<string>('');

  // Bulk operations state
  const [selectedRuleIds, setSelectedRuleIds] = useState<Set<string>>(new Set());

  // Delete confirmation modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [ruleToDelete, setRuleToDelete] = useState<{ id: string; name: string } | null>(null);
  const [bulkDeleteModalOpen, setBulkDeleteModalOpen] = useState(false);

  const { success, error: showError } = useToast();

  // Get unique objects from rules
  const availableObjects = useMemo(() => {
    const objects = new Set(rules.map(rule => rule.objectApiName));
    return Array.from(objects).sort();
  }, [rules]);

  // Filter rules based on search and filters
  const filteredRules = useMemo(() => {
    return rules.filter(rule => {
      const matchesSearch =
        rule.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        rule.description?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesObject = objectFilter === 'all' || rule.objectApiName === objectFilter;
      const matchesStatus =
        statusFilter === 'all' ||
        (statusFilter === 'active' && rule.isActive) ||
        (statusFilter === 'inactive' && !rule.isActive);

      return matchesSearch && matchesObject && matchesStatus;
    });
  }, [rules, searchTerm, objectFilter, statusFilter]);

  const handleToggleStatus = async (id: string) => {
    const rule = rules.find(r => r.id === id);
    if (!rule) return;
    const updated = await workflowRuleRouter.updateRuleAsync(id, { isActive: !rule.isActive });
    if (updated) loadRules();
  };

  const handleClone = async (id: string) => {
    const original = rules.find(r => r.id === id);
    if (!original) return;
    const cloned = await workflowRuleRouter.createRuleAsync({
      ...original,
      name: `${original.name} (Copy)`,
      isActive: false,
      createdBy: original.createdBy,
      lastModifiedBy: original.lastModifiedBy
    });
    loadRules();
    success('Workflow Cloned', `"${cloned.name}" has been created`);
    setSelectedRuleId(cloned.id);
    setViewMode('edit');
  };

  const handleDelete = (id: string, name: string) => {
    setRuleToDelete({ id, name });
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (ruleToDelete) {
      const deleted = await workflowRuleRouter.deleteRuleAsync(ruleToDelete.id);
      if (deleted) {
        loadRules();
        success('Workflow Deleted', `"${ruleToDelete.name}" has been deleted`);
      }
      setDeleteModalOpen(false);
      setRuleToDelete(null);
    }
  };

  const handleEdit = (id: string) => {
    setSelectedRuleId(id);
    setViewMode('edit');
  };

  const handleNewRule = () => {
    setSelectedRuleId(undefined);
    setViewMode('new');
  };

  const handleCloseWizard = () => {
    setViewMode('list');
    setSelectedRuleId(undefined);
    loadRules();
  };

  const handleTestWorkflow = (ruleId: string) => {
    const rule = rules.find(r => r.id === ruleId);
    if (rule) {
      setTestRuleId(ruleId);
      setTestRecordId('');
      setTestResult('');
      setIsTestModalOpen(true);
    }
  };

  const handleExecuteTest = async () => {
    if (!testRecordId.trim()) {
      setTestResult('⚠️ Please enter a record ID');
      return;
    }

    const rule = rules.find(r => r.id === testRuleId);
    if (!rule) {
      setTestResult('❌ Rule not found');
      return;
    }

    try {
      setTestResult('⏳ Executing workflow...');

      // Get the record from localStorage
      const storageKey = getStorageKey(rule.objectApiName);
      const recordsData = localStorage.getItem(storageKey);

      if (!recordsData) {
        setTestResult(`❌ No data found for ${rule.objectApiName}. Storage key: ${storageKey}`);
        return;
      }

      const records = JSON.parse(recordsData);
      const record = records.find((r: any) => r.id === testRecordId);

      if (!record) {
        setTestResult(`❌ Record ${testRecordId} not found in ${rule.objectApiName}. Found ${records.length} records in storage.`);
        return;
      }

      // Execute the workflow
      console.log('\n🧪 TEST WORKFLOW EXECUTION (Manual Trigger)');
      await workflowExecutionService.executeWorkflows(
        rule.objectApiName,
        record,
        testTriggerType
      );

      setTestResult(`✅ Workflow executed successfully!\n\nCheck browser console (F12) for detailed execution log.\n\nRecord: ${testRecordId}\nTrigger: ${testTriggerType}\nRule: ${rule.name}`);

      loadRules();
    } catch (error) {
      console.error('Test execution error:', error);
      setTestResult(`❌ Error executing workflow:\n${error instanceof Error ? error.message : 'Unknown error'}\n\nCheck browser console for details.`);
    }
  };

  const getStorageKey = (objectApiName: string): string => {
    const keyMap: Record<string, string> = {
      'Client': 'crm_client_records',
      'Lead': 'crm_lead_records',
      'Contact': 'crm_contact_records',
      'Opportunity': 'crm_opportunity_records',
      'Case': 'crm_case_records',
      'Task': 'crm_task_records',
      'Event': 'crm_event_records',
      'Report': 'crm_report_records'
    };
    return keyMap[objectApiName] || `crm_${objectApiName.toLowerCase()}_records`;
  };

  // Bulk operations handlers
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedRuleIds(new Set(filteredRules.map(r => r.id)));
    } else {
      setSelectedRuleIds(new Set());
    }
  };

  const handleSelectRule = (ruleId: string, checked: boolean) => {
    const newSelection = new Set(selectedRuleIds);
    if (checked) {
      newSelection.add(ruleId);
    } else {
      newSelection.delete(ruleId);
    }
    setSelectedRuleIds(newSelection);
  };

  const handleBulkActivate = async () => {
    if (selectedRuleIds.size === 0) return;
    const count = selectedRuleIds.size;
    await Promise.all(
      Array.from(selectedRuleIds).map(id => {
        const rule = rules.find(r => r.id === id);
        return rule && !rule.isActive ? workflowRuleRouter.updateRuleAsync(id, { isActive: true }) : Promise.resolve(null);
      })
    );
    loadRules();
    setSelectedRuleIds(new Set());
    success('Rules Activated', `${count} workflow rule${count > 1 ? 's' : ''} activated`);
  };

  const handleBulkDeactivate = async () => {
    if (selectedRuleIds.size === 0) return;
    const count = selectedRuleIds.size;
    await Promise.all(
      Array.from(selectedRuleIds).map(id => {
        const rule = rules.find(r => r.id === id);
        return rule && rule.isActive ? workflowRuleRouter.updateRuleAsync(id, { isActive: false }) : Promise.resolve(null);
      })
    );
    loadRules();
    setSelectedRuleIds(new Set());
    success('Rules Deactivated', `${count} workflow rule${count > 1 ? 's' : ''} deactivated`);
  };

  const handleBulkDelete = () => {
    if (selectedRuleIds.size === 0) return;
    setBulkDeleteModalOpen(true);
  };

  const confirmBulkDelete = async () => {
    const count = selectedRuleIds.size;
    await Promise.all(Array.from(selectedRuleIds).map(id => workflowRuleRouter.deleteRuleAsync(id)));
    loadRules();
    setSelectedRuleIds(new Set());
    setBulkDeleteModalOpen(false);
    success('Rules Deleted', `${count} workflow rule${count > 1 ? 's' : ''} deleted`);
  };

  const isAllSelected = filteredRules.length > 0 && selectedRuleIds.size === filteredRules.length;
  const isSomeSelected = selectedRuleIds.size > 0 && selectedRuleIds.size < filteredRules.length;

  const getTriggerTypeLabel = (triggerType: string): string => {
    switch (triggerType) {
      case 'onCreateOnly': return 'On Create';
      case 'onUpdateOnly': return 'On Update';
      case 'onCreateAndUpdate': return 'On Create & Update';
      default: return triggerType;
    }
  };

  const getActionsSummary = (rule: WorkflowRule): string => {
    const actionCounts: Record<string, number> = {};
    rule.actions.forEach(action => {
      actionCounts[action.type] = (actionCounts[action.type] || 0) + 1;
    });

    const summaries: string[] = [];
    if (actionCounts.fieldUpdate) summaries.push(`${actionCounts.fieldUpdate} Field Update${actionCounts.fieldUpdate > 1 ? 's' : ''}`);
    if (actionCounts.emailAlert) summaries.push(`${actionCounts.emailAlert} Email${actionCounts.emailAlert > 1 ? 's' : ''}`);
    if (actionCounts.task) summaries.push(`${actionCounts.task} Task${actionCounts.task > 1 ? 's' : ''}`);
    if (actionCounts.notification) summaries.push(`${actionCounts.notification} Notification${actionCounts.notification > 1 ? 's' : ''}`);
    if (actionCounts.createRecord) summaries.push(`${actionCounts.createRecord} Create Record${actionCounts.createRecord > 1 ? 's' : ''}`);
    if (actionCounts.webhook) summaries.push(`${actionCounts.webhook} Webhook${actionCounts.webhook > 1 ? 's' : ''}`);

    return summaries.join(', ') || 'No actions';
  };

  const getConditionsSummary = (rule: WorkflowRule): string => {
    const count = rule.evaluationCriteria.conditions.length;
    return `${count} condition${count !== 1 ? 's' : ''} (${rule.evaluationCriteria.operator})`;
  };

  const formatDate = (dateString: string | undefined): string => {
    if (!dateString) return 'Never';

    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

      if (diffDays === 0) return 'Today';
      if (diffDays === 1) return '1 day ago';
      if (diffDays < 7) return `${diffDays} days ago`;
      if (diffDays < 30) {
        const weeks = Math.floor(diffDays / 7);
        return `${weeks} week${weeks > 1 ? 's' : ''} ago`;
      }
      if (diffDays < 365) {
        const months = Math.floor(diffDays / 30);
        return `${months} month${months > 1 ? 's' : ''} ago`;
      }

      return date.toLocaleDateString();
    } catch (error) {
      console.error('Error formatting date:', dateString, error);
      return 'Unknown';
    }
  };

  // Show wizard if in new or edit mode
  if (viewMode === 'new' || viewMode === 'edit') {
    return (
      <WorkflowRuleWizard
        mode={viewMode}
        ruleId={selectedRuleId}
        onClose={handleCloseWizard}
      />
    );
  }

  // Show list view
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            Workflow Rules
          </h2>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            Automate processes with rules that trigger actions based on record changes
          </p>
        </div>
        <div className="flex gap-3">
          <StandardButton
            variant="secondary"
            leftIcon={<ExternalLink className="w-4 h-4" />}
            onClick={() => window.open('https://help.salesforce.com/s/articleView?id=sf.workflow_rules_new.htm', '_blank')}
          >
            Help
          </StandardButton>
          <StandardButton
            variant="primary"
            leftIcon={<Plus className="w-4 h-4" />}
            onClick={handleNewRule}
            data-testid="new-workflow-rule-btn"
          >
            New Rule
          </StandardButton>
        </div>
      </div>

      {/* Dev Mode Banner */}
      <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-500 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
              Development Mode
            </p>
            <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
              Showing {rules.length} workflow rules persisted via{' '}
              {USE_GOBODO_API ? 'Gobodo API' : 'mockDataManager.ts (localStorage)'}.
              Changes survive page refresh.
            </p>
          </div>
        </div>
      </div>

      {/* Bulk Actions Bar */}
      {selectedRuleIds.size > 0 && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="text-sm font-medium text-blue-900 dark:text-blue-200">
              {selectedRuleIds.size} rule{selectedRuleIds.size > 1 ? 's' : ''} selected
            </div>
            <div className="flex items-center gap-3">
              <StandardButton
                size="sm"
                variant="secondary"
                leftIcon={<CheckCircle2 className="w-4 h-4" />}
                onClick={handleBulkActivate}
              >
                Activate
              </StandardButton>
              <StandardButton
                size="sm"
                variant="secondary"
                leftIcon={<Power className="w-4 h-4" />}
                onClick={handleBulkDeactivate}
              >
                Deactivate
              </StandardButton>
              <StandardButton
                size="sm"
                variant="danger"
                leftIcon={<Trash2 className="w-4 h-4" />}
                onClick={handleBulkDelete}
              >
                Delete
              </StandardButton>
              <StandardButton
                size="sm"
                variant="ghost"
                onClick={() => setSelectedRuleIds(new Set())}
              >
                Clear Selection
              </StandardButton>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="flex-1 min-w-[200px]">
          <StandardSearchBar
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="Search workflow rules..."
          />
        </div>
        <div className="w-[180px]">
          <StandardSelect
            value={objectFilter}
            onChange={(e) => setObjectFilter(e.target.value)}
            options={[
              { value: 'all', label: 'All Objects' },
              ...availableObjects.map(obj => ({
                value: obj,
                label: obj
              }))
            ]}
          />
        </div>
        <div className="w-[150px]">
          <StandardSelect
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            options={[
              { value: 'all', label: 'All Status' },
              { value: 'active', label: 'Active' },
              { value: 'inactive', label: 'Inactive' }
            ]}
          />
        </div>
      </div>

      {/* Rules List */}
      {filteredRules.length === 0 ? (
        <StandardEmptyState
          icon={<AlertCircle className="w-12 h-12" />}
          title="No workflow rules found"
          message={
            searchTerm || objectFilter !== 'all' || statusFilter !== 'all'
              ? 'Try adjusting your filters or search term.'
              : 'Get started by creating your first workflow rule.'
          }
          actionLabel={rules.length === 0 ? "Create Workflow Rule" : undefined}
          onAction={rules.length === 0 ? handleNewRule : undefined}
        />
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900">
              <tr>
                <th scope="col" className="px-6 py-3">
                  <input
                    type="checkbox"
                    checked={isAllSelected}
                    ref={(el) => {
                      if (el) el.indeterminate = isSomeSelected;
                    }}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                  />
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Status
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Rule Name
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Object
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Trigger
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Criteria
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Executions
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {filteredRules.map(rule => (
                <tr key={rule.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  {/* Checkbox */}
                  <td className="px-6 py-4">
                    <input
                      type="checkbox"
                      checked={selectedRuleIds.has(rule.id)}
                      onChange={(e) => handleSelectRule(rule.id, e.target.checked)}
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                    />
                  </td>

                  {/* Status */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      onClick={() => handleToggleStatus(rule.id)}
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                        rule.isActive
                          ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                          : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-400'
                      }`}
                      title="Click to toggle"
                    >
                      {rule.isActive ? (
                        <>
                          <CheckCircle2 className="w-3 h-3" />
                          Active
                        </>
                      ) : (
                        <>
                          <Power className="w-3 h-3" />
                          Inactive
                        </>
                      )}
                    </button>
                  </td>

                  {/* Rule Name */}
                  <td className="px-6 py-4">
                    <div className="flex flex-col">
                      <button
                        onClick={() => handleEdit(rule.id)}
                        className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline text-left"
                      >
                        {rule.name}
                      </button>
                      {rule.description && (
                        <span className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                          {rule.description}
                        </span>
                      )}
                      {/* Metadata */}
                      <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                        Modified {formatDate(rule.lastModified)}
                        {rule.lastExecuted && ` • Last executed ${formatDate(rule.lastExecuted)}`}
                      </div>
                    </div>
                  </td>

                  {/* Object */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">
                      {rule.objectApiName}
                    </span>
                  </td>

                  {/* Trigger */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900 dark:text-white">
                      {getTriggerTypeLabel(rule.triggerType)}
                    </span>
                  </td>

                  {/* Criteria */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {getConditionsSummary(rule)}
                    </span>
                  </td>

                  {/* Actions */}
                  <td className="px-6 py-4">
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {getActionsSummary(rule)}
                    </span>
                  </td>

                  {/* Executions */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900 dark:text-white">
                      {rule.executionCount || 0}
                    </span>
                  </td>

                  {/* Action Buttons */}
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => handleTestWorkflow(rule.id)}
                        className="text-gray-600 hover:text-purple-600 dark:text-gray-400 dark:hover:text-purple-400"
                        title="Test Workflow"
                      >
                        <Play className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleEdit(rule.id)}
                        className="text-gray-600 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400"
                        title="Edit"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleClone(rule.id)}
                        className="text-gray-600 hover:text-green-600 dark:text-gray-400 dark:hover:text-green-400"
                        title="Clone"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(rule.id, rule.name)}
                        className="text-gray-600 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Test Workflow Modal */}
      <StandardModal
        isOpen={isTestModalOpen}
        onClose={() => setIsTestModalOpen(false)}
        title="Test Workflow"
        size="md"
      >
        <StandardModal.Body>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Rule
              </label>
              <div className="text-sm text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                {rules.find(r => r.id === testRuleId)?.name}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Select Record
              </label>
              {(() => {
                const rule = rules.find(r => r.id === testRuleId);
                const availableRecords = rule ? getRecordsWithIds(rule.objectApiName) : [];

                if (availableRecords.length === 0) {
                  return (
                    <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                      <p className="text-sm text-yellow-800 dark:text-yellow-200">
                        No {rule?.objectApiName} records found in storage.
                        Create a {rule?.objectApiName} record first, then test this workflow.
                      </p>
                    </div>
                  );
                }

                return (
                  <>
                    <StandardSelect
                      value={testRecordId}
                      onChange={(e) => setTestRecordId(e.target.value)}
                      options={[
                        { value: '', label: 'Choose a record...' },
                        ...availableRecords.map(record => ({
                          value: record.id,
                          label: record.label
                        }))
                      ]}
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Found {availableRecords.length} {rule?.objectApiName} record{availableRecords.length !== 1 ? 's' : ''} in storage
                    </p>
                  </>
                );
              })()}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Trigger Type
              </label>
              <StandardSelect
                value={testTriggerType}
                onChange={(e) => setTestTriggerType(e.target.value as 'create' | 'update')}
                options={[
                  { value: 'create', label: 'Create' },
                  { value: 'update', label: 'Update' }
                ]}
              />
            </div>

            {testResult && (
              <div className={`p-3 rounded-lg text-sm whitespace-pre-wrap ${
                testResult.startsWith('✅')
                  ? 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-200'
                  : testResult.startsWith('❌')
                  ? 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200'
                  : 'bg-blue-50 dark:bg-blue-900/20 text-blue-800 dark:text-blue-200'
              }`}>
                {testResult}
              </div>
            )}
          </div>
        </StandardModal.Body>
        <StandardModal.Footer>
          <StandardButton
            variant="secondary"
            onClick={() => setIsTestModalOpen(false)}
          >
            Close
          </StandardButton>
          <StandardButton
            variant="primary"
            leftIcon={<Play className="w-4 h-4" />}
            onClick={handleExecuteTest}
          >
            Execute Test
          </StandardButton>
        </StandardModal.Footer>
      </StandardModal>

      {/* Delete Confirmation Modal */}
      <StandardModal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setRuleToDelete(null);
        }}
        title="Delete Workflow Rule"
        size="md"
      >
        <StandardModal.Body>
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
              <AlertCircle className="text-red-600 dark:text-red-400" size={20} />
            </div>
            <div>
              <p className="text-gray-900 dark:text-gray-100 font-medium mb-2">
                Are you sure you want to delete "{ruleToDelete?.name}"?
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                This action cannot be undone.
              </p>
            </div>
          </div>
        </StandardModal.Body>
        <StandardModal.Footer>
          <StandardButton
            variant="secondary"
            onClick={() => {
              setDeleteModalOpen(false);
              setRuleToDelete(null);
            }}
          >
            Cancel
          </StandardButton>
          <StandardButton
            variant="danger"
            onClick={confirmDelete}
          >
            Delete
          </StandardButton>
        </StandardModal.Footer>
      </StandardModal>

      {/* Bulk Delete Confirmation Modal */}
      <StandardModal
        isOpen={bulkDeleteModalOpen}
        onClose={() => setBulkDeleteModalOpen(false)}
        title="Delete Selected Rules"
        size="md"
      >
        <StandardModal.Body>
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
              <AlertCircle className="text-red-600 dark:text-red-400" size={20} />
            </div>
            <div>
              <p className="text-gray-900 dark:text-gray-100 font-medium mb-2">
                Delete {selectedRuleIds.size} selected workflow rule{selectedRuleIds.size > 1 ? 's' : ''}?
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                This action cannot be undone.
              </p>
            </div>
          </div>
        </StandardModal.Body>
        <StandardModal.Footer>
          <StandardButton
            variant="secondary"
            onClick={() => setBulkDeleteModalOpen(false)}
          >
            Cancel
          </StandardButton>
          <StandardButton
            variant="danger"
            onClick={confirmBulkDelete}
          >
            Delete
          </StandardButton>
        </StandardModal.Footer>
      </StandardModal>
    </div>
  );
}
